/**
 * Chat Rooms Store - AI Focus v4.8
 * Vuex module para Chat Rooms (comunicação interna)
 * Atualizado para Vue 3 - sem Vue.set/Vue.delete
 */

import ChatRoomsAPI from '../../api/chatRooms';

// Estado inicial
const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingMessages: false,
    isFetchingMoreMessages: false,
    isSendingMessage: false,
  },
  messagesPagination: {},
};

// Getters
const getters = {
  getChatRooms: $state => {
    return Object.values($state.records).sort((a, b) => {
      const aTime = a.last_message_at || a.created_at;
      const bTime = b.last_message_at || b.created_at;
      return new Date(bTime) - new Date(aTime);
    });
  },
  getChatRoom: $state => roomId => {
    return $state.records[roomId] || {};
  },
  getUIFlags: $state => $state.uiFlags,
  getTotalUnreadCount: $state => {
    return Object.values($state.records).reduce((total, room) => {
      return total + (room.unread_count || 0);
    }, 0);
  },
  getRoomMessages: $state => roomId => {
    const room = $state.records[roomId];
    return room?.messages || [];
  },
  getRoomMessagesPagination: $state => roomId => {
    return $state.messagesPagination[roomId] || {};
  },
};

// Mutations - Vue 3 compatible (no Vue.set/Vue.delete needed)
const mutations = {
  SET_CHAT_ROOMS($state, chatRooms) {
    chatRooms.forEach(room => {
      $state.records[room.id] = {
        ...$state.records[room.id],
        ...room,
      };
    });
  },
  SET_CHAT_ROOM_ITEM($state, room) {
    $state.records[room.id] = {
      ...$state.records[room.id],
      ...room,
    };
  },
  DELETE_CHAT_ROOM($state, roomId) {
    delete $state.records[roomId];
  },
  SET_UI_FLAG($state, { flag, value }) {
    $state.uiFlags[flag] = value;
  },
  SET_MESSAGES($state, { roomId, messages, pagination }) {
    const room = $state.records[roomId];
    if (room) {
      room.messages = messages;
      $state.messagesPagination[roomId] = pagination;
    }
  },
  ADD_MESSAGE($state, { roomId, message }) {
    const room = $state.records[roomId];
    if (room) {
      if (!room.messages) {
        room.messages = [];
      }
      // Check if message already exists (by id or echo_id) to prevent duplicates
      const existingIndex = room.messages.findIndex(
        m =>
          (message.id && m.id === message.id) ||
          (message.echo_id && m.echo_id === message.echo_id) ||
          (message.echo_id && m.id === message.echo_id) ||
          (m.echo_id && message.id === m.echo_id)
      );
      if (existingIndex === -1) {
        // Message doesn't exist, add it
        room.messages.push(message);
      } else {
        // Message exists, update it instead (in case server has more data)
        room.messages[existingIndex] = {
          ...room.messages[existingIndex],
          ...message,
        };
      }
    }
  },
  UPDATE_MESSAGE($state, { roomId, message }) {
    const room = $state.records[roomId];
    if (room?.messages) {
      const index = room.messages.findIndex(m => m.id === message.id);
      if (index !== -1) {
        room.messages[index] = message;
      }
    }
  },
  SET_MEMBERS($state, { roomId, members }) {
    const room = $state.records[roomId];
    if (room) {
      room.members = members;
    }
  },
  INCREMENT_UNREAD_COUNT($state, roomId) {
    const room = $state.records[roomId];
    if (room) {
      room.unread_count = (room.unread_count || 0) + 1;
    }
  },
  RESET_UNREAD_COUNT($state, roomId) {
    const room = $state.records[roomId];
    if (room) {
      room.unread_count = 0;
    }
  },
  CLEAR_CHAT_ROOMS($state) {
    $state.records = {};
  },
};

// Actions
const actions = {
  async get({ commit }) {
    commit('SET_UI_FLAG', { flag: 'isFetching', value: true });
    try {
      const response = await ChatRoomsAPI.get();
      commit('SET_CHAT_ROOMS', response.data);
    } finally {
      commit('SET_UI_FLAG', { flag: 'isFetching', value: false });
    }
  },

  async show({ commit }, roomId) {
    commit('SET_UI_FLAG', { flag: 'isFetching', value: true });
    try {
      const response = await ChatRoomsAPI.show(roomId);
      commit('SET_CHAT_ROOM_ITEM', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isFetching', value: false });
    }
  },

  async create({ commit }, data) {
    commit('SET_UI_FLAG', { flag: 'isCreating', value: true });
    try {
      const response = await ChatRoomsAPI.create(data);
      commit('SET_CHAT_ROOM_ITEM', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isCreating', value: false });
    }
  },

  async update({ commit }, { roomId, ...data }) {
    commit('SET_UI_FLAG', { flag: 'isUpdating', value: true });
    try {
      const response = await ChatRoomsAPI.update(roomId, data);
      commit('SET_CHAT_ROOM_ITEM', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isUpdating', value: false });
    }
  },

  async delete({ commit }, roomId) {
    commit('SET_UI_FLAG', { flag: 'isDeleting', value: true });
    try {
      await ChatRoomsAPI.delete(roomId);
      commit('DELETE_CHAT_ROOM', roomId);
    } finally {
      commit('SET_UI_FLAG', { flag: 'isDeleting', value: false });
    }
  },

  async markAsRead({ commit }, roomId) {
    try {
      await ChatRoomsAPI.markAsRead(roomId);
      commit('RESET_UNREAD_COUNT', roomId);
    } catch (error) {
      // Log error but don't throw - marking as read is not critical
      // eslint-disable-next-line no-console
      console.warn('[ChatRooms] Failed to mark room as read:', error?.message);
    }
  },

  async updateAvatar({ commit }, { roomId, file }) {
    const response = await ChatRoomsAPI.updateAvatar(roomId, file);
    commit('SET_CHAT_ROOM_ITEM', response.data);
    return response.data;
  },

  async deleteAvatar({ commit }, roomId) {
    const response = await ChatRoomsAPI.deleteAvatar(roomId);
    commit('SET_CHAT_ROOM_ITEM', response.data);
    return response.data;
  },

  async getMembers({ commit }, roomId) {
    const response = await ChatRoomsAPI.getMembers(roomId);
    commit('SET_MEMBERS', { roomId, members: response.data });
    return response.data;
  },

  async addMembers({ dispatch }, { roomId, memberIds }) {
    await ChatRoomsAPI.addMembers(roomId, memberIds);
    await dispatch('getMembers', roomId);
  },

  async removeMember({ dispatch }, { roomId, memberId }) {
    await ChatRoomsAPI.removeMember(roomId, memberId);
    await dispatch('getMembers', roomId);
  },

  async fetchMessages({ commit }, { roomId, before, after }) {
    commit('SET_UI_FLAG', { flag: 'isFetchingMessages', value: true });
    try {
      const response = await ChatRoomsAPI.fetchMessages(roomId, {
        before,
        after,
      });
      const { messages, meta } = response.data;
      commit('SET_MESSAGES', {
        roomId,
        messages,
        pagination: meta,
      });
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isFetchingMessages', value: false });
    }
  },

  async fetchMoreMessages({ commit, state: $state }, { roomId, before }) {
    commit('SET_UI_FLAG', { flag: 'isFetchingMoreMessages', value: true });
    try {
      const response = await ChatRoomsAPI.fetchMessages(roomId, { before });
      const { messages: newMessages, meta } = response.data;
      const room = $state.records[roomId];
      const existingMessages = room?.messages || [];

      // Deduplicate messages by id to prevent duplicates
      const existingIds = new Set(existingMessages.map(m => m.id));
      const uniqueNewMessages = newMessages.filter(m => !existingIds.has(m.id));

      commit('SET_MESSAGES', {
        roomId,
        messages: [...uniqueNewMessages, ...existingMessages],
        pagination: meta,
      });
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isFetchingMoreMessages', value: false });
    }
  },

  async sendMessage({ commit }, { roomId, content, echoId, replyTo }) {
    commit('SET_UI_FLAG', { flag: 'isSendingMessage', value: true });
    try {
      const messageData = {
        content,
        echo_id: echoId,
      };
      // Add reply-to in content_attributes format expected by backend
      if (replyTo) {
        messageData.content_attributes = { in_reply_to: replyTo };
      }
      const response = await ChatRoomsAPI.sendMessage(roomId, messageData);
      commit('ADD_MESSAGE', { roomId, message: response.data });
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isSendingMessage', value: false });
    }
  },

  async sendMessageWithAttachment(
    { commit },
    { roomId, files, content, replyTo }
  ) {
    commit('SET_UI_FLAG', { flag: 'isSendingMessage', value: true });
    try {
      const response = await ChatRoomsAPI.sendMessageWithAttachment(
        roomId,
        files,
        content,
        replyTo
      );
      commit('ADD_MESSAGE', { roomId, message: response.data });
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { flag: 'isSendingMessage', value: false });
    }
  },

  incrementUnreadCount({ commit }, roomId) {
    commit('INCREMENT_UNREAD_COUNT', roomId);
  },

  updateMessage({ commit, state: $state }, message) {
    // Find the room this message belongs to
    const roomId = message.chat_room_id || message.conversation_id;
    if (!roomId) return;

    const room = $state.records[roomId];
    if (!room?.messages) return;

    // Find message by id or echo_id
    const existingIndex = room.messages.findIndex(
      m =>
        (message.id && m.id === message.id) ||
        (message.echo_id &&
          (m.id === message.echo_id || m.echo_id === message.echo_id))
    );

    if (existingIndex !== -1) {
      commit('UPDATE_MESSAGE', { roomId, message });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
